import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
const PAGE_SIZE = 10;

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const SearchToolbar = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 420px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.45rem 0.7rem;
  font-size: 0.88rem;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: none;
    width: 100%;
  }
`;

const SecondaryBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 0.55rem;
  padding: 0.45rem 0.85rem;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  color: #475569;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 0.65rem;
  padding: 0.5rem 1rem;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  background: rgba(99, 102, 241, 0.95);
  color: #f8fafc;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ComposePanel = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  background: rgba(15, 23, 42, 0.03);
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 0.35rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.92rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 140px;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.55rem 0.65rem;
  font-size: 0.92rem;
  resize: vertical;
`;

const ComposeFooter = styled.div`
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem 1.5rem;
`;

const ComposeOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.75rem;
`;

const TypeField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TypeLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.88rem;
  min-width: 7rem;
  background: #fff;
`;

const PinField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  user-select: none;
  margin: 0;
`;

const SubmitArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
`;

const ComposeError = styled.span`
  font-size: 0.85rem;
  color: #b91c1c;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Th = styled.th`
  background: #f8fafc;
  color: #64748b;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.65rem 0.75rem;
  white-space: nowrap;
`;

const ThCenter = styled(Th)`
  text-align: center;
  width: 2.5rem;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
`;

const ThNum = styled(Th)`
  text-align: right;
  width: 4rem;
`;

const ThSeq = styled(Th)`
  text-align: center;
  width: 3rem;
  font-variant-numeric: tabular-nums;
`;

const Td = styled.td`
  border-top: 1px solid #f1f5f9;
  color: #1f2937;
  font-size: 0.86rem;
  padding: 0.65rem 0.75rem;
  vertical-align: middle;
`;

const TdCenter = styled(Td)`
  text-align: center;
`;

const TdNum = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #475569;
`;

const TdSeq = styled(Td)`
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: #64748b;
  font-weight: 600;
`;

const TitleLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  font-weight: ${({ $pinned }) => ($pinned ? 800 : 600)};
  color: ${({ $pinned }) => ($pinned ? '#1d4ed8' : '#0f172a')};
  text-decoration: none;
  line-height: 1.35;
  &:hover {
    text-decoration: underline;
    color: #4f46e5;
  }
`;

const TitlePin = styled.span`
  flex-shrink: 0;
  font-size: 1.05rem;
  line-height: 1;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.12rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 800;
  color: ${({ $type }) => ($type === '중요' ? '#b91c1c' : '#334155')};
  background: ${({ $type }) => ($type === '중요' ? '#fee2e2' : '#f1f5f9')};
`;

const AuthorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Avatar = styled.span`
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: rgba(248, 250, 252, 0.9);
  flex-shrink: 0;
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const AuthorPrimary = styled.span`
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const AuthorSecondary = styled.span`
  font-size: 0.72rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const IconCellInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const MobileList = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: grid;
    gap: 0.55rem;
    padding: 0.6rem;
  }
`;

const MobileCard = styled.article`
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem;
  background: #fff;
`;

const MobileTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
`;

const MobileSeq = styled.span`
  flex-shrink: 0;
  min-width: 1.5rem;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
`;

const MobileMetaGrid = styled.div`
  margin-top: 0.55rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 0.55rem;
  font-size: 0.8rem;
  color: #475569;
`;

const Pager = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.75rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const PageBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 0.6rem;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
  font-size: 0.85rem;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 1.5rem;
  color: #64748b;
  text-align: center;
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

/** 안 읽음: 빨간 점 / 읽음: 자리만 유지(연한 표시) */
const NoticeUnreadIndicator = ({ read }) =>
  read ? (
    <IconCellInner title="읽음" aria-label="읽음" role="img">
      <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true">
        <circle cx="5" cy="5" r="3" fill="#e2e8f0" />
      </svg>
    </IconCellInner>
  ) : (
    <IconCellInner title="새 글" aria-label="읽지 않음" role="img">
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <circle cx="6" cy="6" r="5" fill="#ef4444" />
      </svg>
    </IconCellInner>
  );

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const isNoticeAdmin = (roles = []) =>
  roles.includes('admin') || roles.includes('super_admin');

const formatDt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const AuthorCell = ({ author }) => {
  const { primary, secondary } = getNoticeAuthorDisplay(author);
  return (
    <AuthorWrap>
      <Avatar aria-hidden>{author?.profile_emoji || '🙂'}</Avatar>
      <AuthorMeta>
        <AuthorPrimary title={primary}>{primary}</AuthorPrimary>
        {secondary ? <AuthorSecondary title={secondary}>{secondary}</AuthorSecondary> : null}
      </AuthorMeta>
    </AuthorWrap>
  );
};

const PortalNoticesPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [notices, setNotices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [noticeType, setNoticeType] = useState('일반');
  const [isPinned, setIsPinned] = useState(false);
  const [composeMsg, setComposeMsg] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const admin = isNoticeAdmin(portalUser?.roles || []);

  const fetchNotices = useCallback(
    async (targetPage) => {
      if (!portalUser?.login_id) return;
      setError('');
      try {
        const qs = new URLSearchParams({
          login_id: portalUser.login_id,
          page: String(targetPage),
          page_size: String(PAGE_SIZE),
        });
        if (appliedSearch) qs.set('q', appliedSearch);
        const data = await request({ endpoint: `${BACKEND_BASE_URL}/api/notices?${qs.toString()}` });
        setNotices(data?.notices || []);
        setTotalPages(Math.max(1, data?.total_pages || 1));
        setTotal(data?.total ?? 0);
      } catch (e) {
        setError(e?.responseData?.message || '공지 목록을 불러오지 못했습니다.');
      }
    },
    [portalUser?.login_id, request, appliedSearch],
  );

  useEffect(() => {
    fetchNotices(page);
  }, [page, fetchNotices]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchDraft.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchDraft('');
    setAppliedSearch('');
    setPage(1);
  };

  const handleSubmitNotice = async (e) => {
    e.preventDefault();
    setComposeMsg('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/notices`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          title: title.trim(),
          content: content.trim(),
          notice_type: noticeType,
          is_pinned: isPinned,
        },
      });
      setTitle('');
      setContent('');
      setNoticeType('일반');
      setIsPinned(false);
      setComposeOpen(false);
      setPage(1);
      await fetchNotices(1);
      setComposeMsg('');
    } catch (err) {
      setComposeMsg(err?.responseData?.message || '공지 등록에 실패했습니다.');
    }
  };

  const emptyMessage = appliedSearch
    ? `"${appliedSearch}"에 대한 검색 결과가 없습니다.`
    : '등록된 공지가 없습니다.';

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="NOTICE"
        title="공지사항"
        description="학원에서 알려 드리는 소식과 안내를 여기에서 확인해 보세요."
      />
      <Toolbar>
        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
          총 <strong>{total}</strong>건
        </span>
        {admin ? (
          <PrimaryButton type="button" onClick={() => setComposeOpen((v) => !v)}>
            {composeOpen ? '작성 닫기' : '공지 작성'}
          </PrimaryButton>
        ) : null}
      </Toolbar>

      <SearchToolbar onSubmit={handleSearchSubmit}>
        <SearchInput
          id="notice-list-search"
          type="search"
          value={searchDraft}
          onChange={(ev) => setSearchDraft(ev.target.value)}
          placeholder="제목·내용 검색"
          autoComplete="off"
          aria-label="공지 제목·내용 검색"
        />
        <SecondaryBtn type="submit" disabled={loading}>
          검색
        </SecondaryBtn>
        {appliedSearch ? (
          <SecondaryBtn type="button" onClick={handleClearSearch} disabled={loading}>
            초기화
          </SecondaryBtn>
        ) : null}
      </SearchToolbar>

      {admin && composeOpen ? (
        <ComposePanel as="form" onSubmit={handleSubmitNotice}>
          <Field>
            <Label htmlFor="notice-title">제목</Label>
            <Input
              id="notice-title"
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              maxLength={200}
              placeholder="제목 (최대 200자)"
              required
            />
          </Field>
          <Field style={{ marginBottom: 0 }}>
            <Label htmlFor="notice-content">내용</Label>
            <TextArea
              id="notice-content"
              value={content}
              onChange={(ev) => setContent(ev.target.value)}
              placeholder="공지 내용을 입력하세요."
              required
            />
          </Field>
          <ComposeFooter>
            <ComposeOptions>
              <TypeField>
                <TypeLabel htmlFor="notice-type">유형</TypeLabel>
                <Select
                  id="notice-type"
                  value={noticeType}
                  onChange={(ev) => setNoticeType(ev.target.value)}
                >
                  <option value="일반">일반</option>
                  <option value="중요">중요</option>
                </Select>
              </TypeField>
              <PinField>
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(ev) => setIsPinned(ev.target.checked)}
                />
                상단 고정
              </PinField>
            </ComposeOptions>
            <SubmitArea>
              <PrimaryButton type="submit" disabled={loading}>
                등록
              </PrimaryButton>
            </SubmitArea>
          </ComposeFooter>
          {composeMsg ? <ComposeError>{composeMsg}</ComposeError> : null}
        </ComposePanel>
      ) : null}

      <TableWrap>
        {notices.length === 0 && !loading ? (
          <Empty>{emptyMessage}</Empty>
        ) : notices.length === 0 ? (
          <Empty>불러오는 중...</Empty>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <ThCenter aria-label="새 글 표시" title="안 읽은 글은 빨간 점">
                    {'\u00a0'}
                  </ThCenter>
                  <ThSeq>No.</ThSeq>
                  <Th>유형</Th>
                  <Th>제목</Th>
                  <Th>작성자</Th>
                  <ThNum>조회</ThNum>
                  <Th>작성일</Th>
                </tr>
              </thead>
              <tbody>
                {notices.map((n, idx) => (
                  <tr key={n.id}>
                    <TdCenter>
                      <NoticeUnreadIndicator read={Boolean(n.read_by_me)} />
                    </TdCenter>
                  <TdSeq>{Math.max(1, total - ((page - 1) * PAGE_SIZE + idx))}</TdSeq>
                    <Td>
                      <TypeBadge $type={n.notice_type}>{n.notice_type}</TypeBadge>
                    </Td>
                    <Td>
                      <TitleLink to={`/portal/notices/${n.id}`} $pinned={n.is_pinned}>
                        {n.is_pinned ? (
                          <TitlePin title="상단 고정" aria-label="상단 고정" role="img">
                            📌
                          </TitlePin>
                        ) : null}
                        <span style={{ minWidth: 0 }}>{n.title}</span>
                      </TitleLink>
                    </Td>
                    <Td>
                      <AuthorCell author={n.author} />
                    </Td>
                    <TdNum>{n.view_count ?? 0}</TdNum>
                    <Td>{formatDt(n.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <MobileList>
              {notices.map((n, idx) => {
                const seq = Math.max(1, total - ((page - 1) * PAGE_SIZE + idx));
                const { primary } = getNoticeAuthorDisplay(n.author);
                return (
                  <MobileCard key={`m-${n.id}`}>
                    <MobileTitleRow>
                      <MobileSeq>No.{seq}</MobileSeq>
                      <TypeBadge $type={n.notice_type}>{n.notice_type}</TypeBadge>
                      <NoticeUnreadIndicator read={Boolean(n.read_by_me)} />
                    </MobileTitleRow>
                    <div style={{ marginTop: '0.4rem' }}>
                      <TitleLink to={`/portal/notices/${n.id}`} $pinned={n.is_pinned}>
                        {n.is_pinned ? (
                          <TitlePin title="상단 고정" aria-label="상단 고정" role="img">
                            📌
                          </TitlePin>
                        ) : null}
                        <span style={{ minWidth: 0 }}>{n.title}</span>
                      </TitleLink>
                    </div>
                    <MobileMetaGrid>
                      <span>작성자: {primary}</span>
                      <span>조회: {n.view_count ?? 0}</span>
                      <span>{formatDt(n.created_at)}</span>
                    </MobileMetaGrid>
                  </MobileCard>
                );
              })}
            </MobileList>
          </>
        )}
      </TableWrap>

      <Pager>
        <PageBtn type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          이전
        </PageBtn>
        <span style={{ fontSize: '0.88rem', color: '#475569' }}>
          {page} / {totalPages}
        </span>
        <PageBtn
          type="button"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          다음
        </PageBtn>
      </Pager>

      {error ? <ErrorText>{error}</ErrorText> : null}
    </Wrapper>
  );
};

export default PortalNoticesPage;
